import { Roles } from './model/roles.model';
import { Component, OnInit, QueryList, ViewChildren } from '@angular/core';
import { RolesService } from './service/roles.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MsgModel } from 'src/app/component/common-components/msg/model/msg.model';
import { GetPropertyPipe } from 'src/app/pipes/get-property.pipe';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';

@Component({
    selector: 'edit-roles',
    templateUrl: './roles.component.html',
    styleUrls: ['./roles.component.scss']
})
export class RolesComponent implements OnInit {
    @ViewChildren(MatPaginator) paginators: QueryList<MatPaginator> = new QueryList<MatPaginator>();
    @ViewChildren(MatSort) sorts: QueryList<MatSort> = new QueryList<MatSort>();

    // Table display
    displayedColumns: string[] = ['EDIT', 'ACTIVE', 'NAME', 'DESCRIPTION'];
    newDisplayedColumns: string[] = ['ADD'];

    // Table results
    tableDataSources: MatTableDataSource<Roles>[] = [];

    // Results
    results: Roles[] = [];
    displayName: string = '';
    filter: string = '';

    isLoading: boolean = false;
    msg: MsgModel = {};

    getPropertyPipe = new GetPropertyPipe();

    // Editing variables
    addingForm!: FormGroup;
    adding: boolean = false;
    editingForm!: FormGroup;
    editingId: number | undefined;

    constructor(private fb: FormBuilder, private rolesService: RolesService) {
        this.addingForm = fb.group({
            active: ['', [Validators.required]],
            name: ['', [Validators.required]],
            description: ['', [Validators.required]]
        });
        this.editingForm = fb.group({
            active: ['', [Validators.required]],
            name: ['', [Validators.required]],
            description: ['', [Validators.required]]
        });
    }

    ngOnInit(): void {
        this.getRoles();
    }

    getRoles() {
        this.isLoading = true;

        this.rolesService.index().subscribe({
            next: (res) => {
                this.results = res.roles;
            },
            error: (err) => {
                this.results = [];
                this.msg = { SUCCESS: false, MSG: 'Error retrieving Roles.' };
            },
            complete: () => {
                // Updates the Tables to be displayed w/ this.results
                this.updateDataSource();

                // Display results
                this.isLoading = false;
            }
        });
    }

    addNewRole(role: Roles) {
        this.rolesService.createRole(role).subscribe({
            next: (res) => {
                this.msg = res.msg;
            },
            error: (err) => {
                this.msg = { SUCCESS: false, MSG: 'Error adding new role.' };
            },
            complete: () => {
                this.getRoles();
            }
        });
    }

    updateRole(role: Roles) {
        this.rolesService.updateRole(role).subscribe({
            next: (res) => {
                this.msg = res.msg;
            },
            error: (err) => {
                this.msg = { SUCCESS: false, MSG: 'Error updating new role.' };
            },
            complete: () => {
                this.getRoles();
            }
        });
    }

    deleteSelectedRole(role: Roles) {
        this.rolesService.deleteRole(role).subscribe({
            next: (res) => {
                this.msg = res.msg;
            },
            error: (err) => {
                this.msg = { SUCCESS: false, MSG: 'Error deleting new role.' };
            },
            complete: () => {
                this.getRoles();
            }
        });
    }

    updateDataSource() {
        this.tableDataSources = [];

        setTimeout(() => {
            // Data
            let dataSource = new MatTableDataSource<Roles>();
            dataSource.data = this.results;

            // Paginator
            //dataSource.paginator = this.paginators.toArray()[0];

            // Sort
            dataSource.sort = this.sorts.toArray()[0];
            const defaultSort: Sort = { active: 'NAME', direction: 'asc' };
            dataSource.sort.active = defaultSort.active;
            dataSource.sort.direction = defaultSort.direction;
            dataSource.sort.sortChange.emit(defaultSort);
            dataSource.sortingDataAccessor = (obj, property) => this.getPropertyPipe.transform(obj, property, true);

            // Filter
            dataSource.filterPredicate = (data: any, filter) => {
                const dataStr = JSON.stringify(data).toLowerCase();
                return dataStr.indexOf(filter) != -1;
            };

            this.tableDataSources.push(dataSource);
        });
    }

    setFilter() {
        this.tableDataSources.forEach((dataSource, index) => {
            dataSource.filter = this.filter.trim().toLocaleLowerCase();
        });
    }

    // Add Function
    addRole() {
        this.newDisplayedColumns = ['ADD_EDIT', 'ADD_ACTIVE', 'ADD_NAME', 'ADD_DESCRIPTION'];
        this.resetAddingRole();
        this.adding = true;
    }
    confirmAddRole() {
        this.addNewRole(this.buildRole(this.addingForm));
        this.cancelAdd();
    }
    cancelAddRole() {
        this.cancelAdd();
    }

    // Edit Function
    editRole(role: Roles) {
        this.setEditingRole(role);
    }
    confirmEditRole() {
        this.updateRole(this.buildRole(this.editingForm));
        this.cancelEdit();
    }
    cancelEditRole() {
        this.cancelEdit();
    }

    // Delete Function
    deleteRole(role: Roles) {
        this.deleteSelectedRole(role);
    }

    // Reset Function
    private cancelAdd() {
        this.resetAddingRole();
        this.newDisplayedColumns = ['ADD'];
        this.adding = false;
    }

    private cancelEdit() {
        this.resetEditingRole();
    }

    private setEditingRole(role: Roles) {
        console.warn(role);
        this.editingId = role.ID;
        this.editingForm.patchValue({ active: role.ACTIVE === 1 ? 1 : role.ACTIVE === 0 ? 0 : 1, name: role.NAME, description: role.DESCRIPTION });
        this.editingForm.markAsPristine();
    }

    private resetAddingRole() {
        this.addingForm.patchValue({ active: 1, name: '', description: '' });
        this.addingForm.markAsPristine();
    }

    private resetEditingRole() {
        this.editingForm.patchValue({ active: 1, name: '', description: '' });
        this.editingId = undefined;
    }

    private buildRole(form: FormGroup): Roles {
        let newRole = new Roles();

        newRole.ID = this.editingId ? this.editingId : undefined;
        newRole.ACTIVE = form.get('active')?.value;
        newRole.NAME = form.get('name')?.value;
        newRole.DESCRIPTION = form.get('description')?.value;

        return newRole;
    }
}
